import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "@mui/material/styles"; // Standard MUI useTheme
import {
  Box,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  Stack,
  Divider,
  Skeleton,
  Fade,
  Paper,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material"; // For transparency
import {
  Description as DescriptionIcon,
  Engineering as BuildIcon,
  Refresh as RefreshIcon,
  ArrowForward as ArrowForwardIcon,
  Visibility as VisibilityIcon,
  AddCircleOutline as AddCircleIcon, // Using Outline for a potentially lighter feel in buttons
  Assignment as AssignmentIcon,
  FactCheck as FactCheckIcon,
  WarningAmber as WarningIcon, // Using Amber version for better visibility with orange
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  NoteAltOutlined as NoteAltIcon, // Using Outline
  Person as PersonIcon,
  Inventory as InventoryIcon,
} from "@mui/icons-material";
import moment from "moment";
import "moment/locale/de";
import "moment/locale/el";
import "moment/locale/bg";
import "moment/locale/en-gb";

import {
  reportService,
  dashboardService,
  excavatorService,
  betriebstageBuchService,
  userService,
} from "../services/api";
import PageContainer from "../components/PageContainer";
import OperatorStats from "../components/OperatorStats";


const Dashboard = () => {
  const { currentUser } = useContext(AuthContext);
  const { t, currentLanguage } = useLanguage();
  const theme = useTheme(); // MUI theme object
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalExcavators: 0,
    activeExcavators: 0,
    totalReports: 0,
    signedReports: 0,
    unsignedReports: 0,
    maintenanceDueCount: 0,
    totalUsers: 0,
    totalBetriebstagebuch: 0,
    unsignedBetriebstagebuch: 0,
    percentChange: {
      excavators: 0,
      reports: 0,
      maintenance: 0,
    },
  });
  const [recentReports, setRecentReports] = useState([]);
  const [recentBetriebstagebuch, setRecentBetriebstageBuch] = useState([]);
  const [operatorStats, setOperatorStats] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [excavators, setExcavators] = useState([]);
  const [inventorySummary, setInventorySummary] = useState([]);

  const handleRefresh = () => {
    fetchDashboardData();
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    setRefreshing(true);
    try {
      const [
        dashboardStatsRes,
        recentReportsRes,
        betriebstagebuchRes,
        excavatorRes,
        inventorySummaryRes,
        usersRes // Conditionally fetched
      ] = await Promise.allSettled([
        dashboardService.getStats(),
        reportService.getAll(currentUser.role === "operator" ? { limit: 5, sortBy: "createdAt", sortOrder: "desc", operator: currentUser._id } : { limit: 5, sortBy: "createdAt", sortOrder: "desc" }),
        betriebstageBuchService.getAll(currentUser.role === "operator" ? { limit: 5, sortBy: "createdAt", sortOrder: "desc", creator: currentUser._id } : { limit: 5, sortBy: "createdAt", sortOrder: "desc" }),
        excavatorService.getAll(),
        dashboardService.getInventorySummary(),
        (currentUser.role === "admin" || currentUser.role === "manager") ? userService.getAll() : Promise.resolve({ value: { data: [] }}) // Resolve with empty if not fetched
      ]);

      const dashboardStats = dashboardStatsRes.status === 'fulfilled' ? dashboardStatsRes.value.data : {};

      const reportsData = recentReportsRes.status === 'fulfilled' ? (recentReportsRes.value.data?.reports || recentReportsRes.value.data || []) : [];
      const processedReports = await Promise.all(
        reportsData.map(async (report) => {
          if (report.excavator && typeof report.excavator === 'string') {
            try {
              const excRes = await excavatorService.getById(report.excavator);
              report.excavator = excRes.data;
            } catch (e) { console.error("Failed to fetch excavator for report", e); }
          }
          return {
            ...report,
            excavatorName: report.excavator ? `${report.excavator.manufacturer || ""} ${report.excavator.model || ""}`.trim() || t("unknownExcavator") : t("unknownExcavator"),
          };
        })
      );
      setRecentReports(processedReports);

      const betriebstageBuchEntries = betriebstagebuchRes.status === 'fulfilled' ? (betriebstagebuchRes.value.data?.data || betriebstagebuchRes.value.data?.entries || betriebstagebuchRes.value.data || []) : [];
      setRecentBetriebstageBuch(betriebstageBuchEntries);

      const excavatorData = excavatorRes.status === 'fulfilled' ? (excavatorRes.value.data?.excavators || excavatorRes.value.data?.data || excavatorRes.value.data || []) : [];
      setExcavators(excavatorData);

      setInventorySummary(inventorySummaryRes.status === 'fulfilled' ? (inventorySummaryRes.value.data || []) : []);

      const userData = usersRes.status === 'fulfilled' ? (usersRes.value.data?.users || usersRes.value.data?.data || usersRes.value.data || []) : [];

      let currentOperatorStats = [];
      if (dashboardStats?.operatorStats) {
        currentOperatorStats = dashboardStats.operatorStats;
      } else if (currentUser.role === "operator") {
        currentOperatorStats = [{ userId: currentUser._id, name: currentUser.name, reportCount: reportsData.length, profileImage: currentUser.profileImage }];
      }
      setOperatorStats(currentOperatorStats.map(op => ({ id: op.userId || op._id, name: op.name || op.userName || t("unknownOperator"), count: op.reportCount || 0, imgSrc: op.profileImage })));

      const signedCount = dashboardStats?.signedReports || reportsData.filter(r => ["signed", "approved"].includes(r.status)).length;
      const unsignedCount = dashboardStats?.unsignedReports || reportsData.filter(r => r.status === "unsigned").length;

      setStats({
        totalExcavators: dashboardStats?.totalExcavators || excavatorData.length || 0,
        activeExcavators: dashboardStats?.activeExcavators || excavatorData.filter(e => e.status === "operational" || e.status === "active").length || 0,
        totalReports: dashboardStats?.totalReports || reportsData.length || 0,
        signedReports: signedCount,
        unsignedReports: unsignedCount,
        maintenanceDueCount: dashboardStats?.maintenanceDueCount || excavatorData.filter(e => e.maintenanceStatus === "overdue").length || 0,
        activeUsers: dashboardStats?.activeUsers || userData.length || 0, // Simplified active users
        totalUsers: userData.length || 0,
        totalBetriebstagebuch: dashboardStats?.totalBetriebstagebuch || betriebstageBuchEntries.length || 0,
        unsignedBetriebstagebuch: dashboardStats?.unsignedBetriebstagebuch || betriebstageBuchEntries.filter(entry => entry.status !== "approved").length || 0,
        percentChange: dashboardStats?.percentChange || { excavators: 0, reports: 0, maintenance: 0 },
      });

    } catch (err) {
      console.error("Error in fetchDashboardData:", err);
      // Minimal error state if needed
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser._id, currentUser.role]); // Fetch data on user change

  const upcomingMaintenances = React.useMemo(() => {
    // ... (memoized calculation remains the same)
    if (!excavators || excavators.length === 0) return [];
    return excavators
      .filter((ex) => ex.nextMaintenanceDate)
      .map((ex) => {
        const name = `${ex.manufacturer || ""} ${ex.model || ""}`.trim();
        const lastMaintenance = ex.lastMaintenanceDate;
        const nextMaintenance = ex.nextMaintenanceDate;
        const daysUntilDue = nextMaintenance
          ? moment(nextMaintenance).diff(moment(), "days")
          : null;
        return {
          id: ex._id,
          name,
          lastMaintenance,
          nextMaintenance,
          daysUntilDue,
        };
      })
      .filter((ex) => ex.nextMaintenance)
      .sort(
        (a, b) => new Date(a.nextMaintenance) - new Date(b.nextMaintenance)
      );
  }, [excavators]);

  const upcomingHourMaintenances = React.useMemo(() => {
    // ... (memoized calculation remains the same)
    if (!excavators || excavators.length === 0) return [];
    return excavators
      .map((ex) => {
        const name = `${ex.manufacturer || ""} ${ex.model || ""}`.trim();
        const hours = ex.hours || 0;
        let nextMaintenanceHours = ex.nextMaintenanceHours;
        if (!nextMaintenanceHours || nextMaintenanceHours <= hours) {
          nextMaintenanceHours = Math.ceil(hours / 500) * 500 + 500;
        }
        const hoursRemaining = nextMaintenanceHours - hours;
        return {
          id: ex._id,
          name,
          hours,
          nextMaintenanceHours,
          hoursRemaining,
        };
      })
      .filter((ex) => ex.hoursRemaining > 0)
      .sort((a, b) => a.hoursRemaining - b.hoursRemaining);
  }, [excavators]);


  if (loading) {
    return (
      <PageContainer title={t("dashboard")}>
        <Grid container spacing={3}>
          {[...Array(4)].map((_, i) => (
            <Grid item xs={12} sm={6} md={3} key={`statSkel-${i}`}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: theme.shape.borderRadius }} />
            </Grid>
          ))}
          <Grid item xs={12} md={7}>
            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: theme.shape.borderRadius }}/>
          </Grid>
          <Grid item xs={12} md={5}>
            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: theme.shape.borderRadius }}/>
          </Grid>
          {[...Array(2)].map((_, i) => (
            <Grid item xs={12} md={6} key={`tableSkel-${i}`}>
              <Skeleton variant="rectangular" height={250} sx={{ borderRadius: theme.shape.borderRadius }}/>
            </Grid>
          ))}
        </Grid>
      </PageContainer>
    );
  }

  const StatCardItem = ({ icon, title, value, subValue, trend, trendColor, color = "primary" }) => (
    <Card sx={{ height: "100%", display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2, p: theme.spacing(2, isSmallScreen ? 1.5 : 2.5) }}>
        <Avatar sx={{ bgcolor: alpha(theme.palette[color].main, 0.15), color: theme.palette[color].main, width: 48, height: 48 }}>
          {icon}
        </Avatar>
        <Box>
          <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', color: theme.palette[color].dark }}>
            {value}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', mb: 0.5 }}>
            {title}
          </Typography>
          {subValue && <Typography variant="caption" color="text.secondary">{subValue}</Typography>}
        </Box>
      </CardContent>
      {trend !== undefined && trend !== 0 && (
        <Box sx={{ px: 2.5, pb: 1.5, pt:0, display: 'flex', alignItems: 'center', color: trendColor }}>
          {trend > 0 ? <TrendingUpIcon sx={{ fontSize: '1rem', mr: 0.5 }}/> : <TrendingDownIcon sx={{ fontSize: '1rem', mr: 0.5 }}/>}
          <Typography variant="caption" sx={{ fontWeight: 'medium' }}>{Math.abs(trend)}% {t('thisMonth')}</Typography>
        </Box>
      )}
    </Card>
  );

  return (
    <PageContainer title={t("dashboard")}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: "bold" }}>
          {t("dashboard")}
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={refreshing}
          variant="outlined" // Changed to outlined for less emphasis than main CTA
          color="secondary"
        >
          {refreshing ? t("refreshing") : t("refresh")}
        </Button>
      </Box>

      <Fade in timeout={500}>
        <Card sx={{
          mb: 3,
          p: { xs: 2, md: 3 },
          backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.9)} 0%, ${alpha(theme.palette.primary.main, 0.8)} 100%)`,
          color: theme.palette.primary.contrastText,
          position: 'relative',
          overflow: 'visible' // For potential decorative elements if added
        }}>
          <Box sx={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100, bgcolor: alpha(theme.palette.common.white, 0.1), borderRadius: '50%', filter: 'blur(10px)', opacity: 0.7 }} />
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h5" fontWeight="bold" sx={{ mb: 0.5 }}>
                {t("welcomeBack")}, <span style={{ color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.primary.contrastText }}>{currentUser?.firstName || currentUser?.name || t("user")}!</span>
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
                {t("dashboardSubtitle") || "Here's what's happening with your fleet today."}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button variant="contained" color="secondary" component={Link} to="/reports/create" startIcon={<AddCircleIcon />}>
                  {t("createNewReport")}
                </Button>
                <Button variant="outlined" component={Link} to="/betriebstagebuch/create" startIcon={<NoteAltIcon />}
                  sx={{ color: theme.palette.primary.contrastText, borderColor: alpha(theme.palette.primary.contrastText, 0.5), '&:hover': { borderColor: theme.palette.primary.contrastText, bgcolor: alpha(theme.palette.common.white, 0.1)} }} >
                  {t("createBetriebstagebuch")}
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" }, mt: {xs: 2, md: 0} }}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>{t("todaysDate")}:</Typography>
              <Typography variant="subtitle1" fontWeight="medium">
                {moment().locale(currentLanguage).format("dddd, D MMMM YYYY")}
              </Typography>
            </Grid>
          </Grid>
        </Card>
      </Fade>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCardItem icon={<BuildIcon />} title={t("excavators")} value={stats.totalExcavators} subValue={`${stats.activeExcavators} ${t("active")}`} color="primary" trend={stats.percentChange?.excavators} trendColor={stats.percentChange?.excavators > 0 ? theme.palette.success.main : theme.palette.error.main } />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCardItem icon={<DescriptionIcon />} title={t("reports")} value={stats.totalReports} subValue={`${stats.unsignedReports} ${t("unsigned")}`} color="info" trend={stats.percentChange?.reports} trendColor={stats.percentChange?.reports > 0 ? theme.palette.success.main : theme.palette.error.main } />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCardItem icon={<FactCheckIcon />} title={t("signedReports")} value={stats.signedReports} color="success" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCardItem icon={<WarningIcon />} title={t("maintenanceDue")} value={stats.maintenanceDueCount} color="warning" trend={stats.percentChange?.maintenance} trendColor={stats.percentChange?.maintenance > 0 ? theme.palette.error.main : theme.palette.success.main } />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <Card sx={{ mb: 3 }}>
            <CardHeader titleTypographyProps={{variant: "h6"}} title={t("recentReports")} action={
              <Button component={Link} to="/reports" endIcon={<ArrowForwardIcon />} size="small">{t("viewAll")}</Button>
            }/>
            <CardContent sx={{p:0}}>
              {recentReports.length === 0 ? (
                <Typography sx={{ p: 2, textAlign: 'center' }} color="text.secondary">{t('noRecentReports')}</Typography>
              ) : (
                <List disablePadding>
                  {recentReports.slice(0,5).map((report, index) => (
                    <ListItem key={report._id} divider={index < recentReports.slice(0,5).length - 1}
                      secondaryAction={
                        <Tooltip title={t('viewDetails')}>
                          <IconButton component={Link} to={`/reports/${report._id}`} size="small" edge="end"><VisibilityIcon /></IconButton>
                        </Tooltip>
                      }
                      sx={{ '&:hover': { bgcolor: theme.palette.action.hover } }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.15), color: theme.palette.info.main }}><DescriptionIcon /></Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={<Typography variant="subtitle2" fontWeight="medium">{report.excavatorName}</Typography>}
                        secondary={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip size="small" label={t(report.status || 'pending')} color={ report.status === 'signed' || report.status === 'approved' ? 'success' : report.status === 'unsigned' ? 'warning' : 'default'}/>
                            <Typography variant="caption" color="text.secondary">{moment(report.createdAt).format('L')}</Typography>
                          </Stack>
                        }/>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardHeader titleTypographyProps={{variant: "h6"}} title={t("recentBetriebstagebuch")} action={
              <Button component={Link} to="/betriebstagebuch" endIcon={<ArrowForwardIcon />} size="small">{t("viewAll")}</Button>
            }/>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{fontWeight: 'bold'}}>{t("date")}</TableCell>
                    <TableCell sx={{fontWeight: 'bold'}}>{t("creator")}</TableCell>
                    <TableCell sx={{fontWeight: 'bold'}}>{t("status")}</TableCell>
                    <TableCell sx={{fontWeight: 'bold'}} align="right">{t("actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentBetriebstagebuch.length === 0 ? (
                    <TableRow><TableCell colSpan={4} align="center"><Typography color="text.secondary" p={2}>{t('noRecentReports')}</Typography></TableCell></TableRow>
                  ) : (
                    recentBetriebstagebuch.slice(0,5).map((entry) => (
                      <TableRow hover key={entry._id}>
                        <TableCell>{moment(entry.date || entry.createdAt).format('L')}</TableCell>
                        <TableCell>{entry.creator?.name || t('unknown')}</TableCell>
                        <TableCell><Chip size="small" label={t(entry.status || 'pending')} color={entry.status === 'approved' ? 'success' : 'default'}/></TableCell>
                        <TableCell align="right">
                          <Tooltip title={t('viewDetails')}><IconButton component={Link} to={`/betriebstagebuch/${entry._id}`} size="small"><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    )))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Card sx={{ mb: 3 }}>
            <CardHeader titleTypographyProps={{variant: "h6"}} title={t("topOperators")} subheader={t("byReportCount")} action={
                <Button component={Link} to="/stats" endIcon={<ArrowForwardIcon />} size="small">{t("viewDetails")}</Button>
            }/>
            <CardContent sx={{pt:0}}>
              <OperatorStats operators={operatorStats} emptyMessage={t("noOperatorData")} />
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardHeader titleTypographyProps={{variant: "h6"}} title={t("upcomingMaintenances")} />
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell sx={{fontWeight: 'bold'}}>{t("machine")}</TableCell>
                  <TableCell sx={{fontWeight: 'bold'}}>{t("nextMaintenanceDate")}</TableCell>
                  <TableCell sx={{fontWeight: 'bold'}} align="right">{t("daysUntilDue")}</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {upcomingMaintenances.length === 0 ? (
                     <TableRow><TableCell colSpan={3} align="center"><Typography color="text.secondary" p={2}>{t('noUpcomingMaintenancesPredicted')}</Typography></TableCell></TableRow>
                  ) : (
                    upcomingMaintenances.slice(0, 5).map((item) => {
                      let color = item.daysUntilDue <= 0 ? "error" : item.daysUntilDue <= 10 ? "warning" : "success";
                      return (
                        <TableRow hover key={item.id}>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>{item.nextMaintenance ? moment(item.nextMaintenance).format('L') : '-'}</TableCell>
                          <TableCell align="right"><Chip size="small" label={item.daysUntilDue <= 0 ? `${Math.abs(item.daysUntilDue)} ${t('daysOverdue')}` : `${item.daysUntilDue} ${t('days')}`} color={color} /></TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
           <Card sx={{ mb: 3 }}>
            <CardHeader titleTypographyProps={{variant: "h6"}} title={`${t("upcomingMaintenances")} (${t("operatingHours")})`} />
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell sx={{fontWeight: 'bold'}}>{t("machine")}</TableCell>
                  <TableCell sx={{fontWeight: 'bold'}}>{t("operatingHours")}</TableCell>
                  <TableCell sx={{fontWeight: 'bold'}} align="right">{t("hoursRemaining")}</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {upcomingHourMaintenances.length === 0 ? (
                     <TableRow><TableCell colSpan={3} align="center"><Typography color="text.secondary" p={2}>{t('noUpcomingMaintenancesPredicted')}</Typography></TableCell></TableRow>
                  ) : (
                    upcomingHourMaintenances.slice(0, 5).map((item) => {
                       let color = item.hoursRemaining <= 0 ? "error" : item.hoursRemaining <= 50 ? "warning" : "success";
                      return (
                        <TableRow hover key={item.id}>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>{item.hours}</TableCell>
                          <TableCell align="right"><Chip size="small" label={item.hoursRemaining <= 0 ? `${Math.abs(item.hoursRemaining)} ${t('hoursOverdue')}` : `${item.hoursRemaining} ${t('hours')}`} color={color} /></TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
      </Grid>
    </PageContainer>
  );
};

export default Dashboard;
